import i18next from "i18next";
import { getGooglePickerZIndex } from "../utils/modalLayer";
import { getLanguageFormatted } from "../utils/string";

const GOOGLE_API_SCRIPT = "https://apis.google.com/js/api.js";
const GOOGLE_GSI_SCRIPT = "https://accounts.google.com/gsi/client";

const PICKER_DIALOG_SELECTOR = ".picker-dialog";
const PICKER_DIALOG_BG_SELECTOR = ".picker-dialog-bg";
const PICKER_LAYER_SELECTORS = `${PICKER_DIALOG_SELECTOR}, ${PICKER_DIALOG_BG_SELECTOR}, .picker`;
const PICKER_Z_INDEX_RETRY_DELAYS_MS = [0, 50, 150, 300, 500];
const PICKER_Z_INDEX_WATCHER_MAX_MS = 5000;

const applyPickerLayerStyles = ({ zIndex }) => {
  const resolvedZIndex = zIndex ?? getGooglePickerZIndex();

  document.querySelectorAll(PICKER_LAYER_SELECTORS).forEach((element) => {
    element.style.setProperty("z-index", String(resolvedZIndex), "important");
  });

  document.querySelectorAll(PICKER_DIALOG_BG_SELECTOR).forEach((element) => {
    element.style.setProperty("position", "fixed", "important");
    element.style.setProperty("top", "0", "important");
    element.style.setProperty("left", "0", "important");
    element.style.setProperty("width", "100%", "important");
    element.style.setProperty("height", "100%", "important");
  });

  document.querySelectorAll(PICKER_DIALOG_SELECTOR).forEach((element) => {
    element.style.setProperty("position", "fixed", "important");
    element.style.setProperty("top", "50%", "important");
    element.style.setProperty("left", "50%", "important");
    element.style.setProperty(
      "transform",
      "translate(-50%, -50%)",
      "important"
    );
    element.style.setProperty("margin", "0", "important");
  });
};

const startPickerZIndexWatcher = ({ zIndex }) => {
  const apply = () => applyPickerLayerStyles({ zIndex });

  apply();

  requestAnimationFrame(() => {
    apply();
    requestAnimationFrame(apply);
  });

  const timeoutIds = PICKER_Z_INDEX_RETRY_DELAYS_MS.map((delay) =>
    window.setTimeout(apply, delay)
  );

  const observer = new MutationObserver(apply);
  observer.observe(document.body, { childList: true, subtree: true });

  let maxWatcherTimeoutId = null;

  const stopWatcher = () => {
    observer.disconnect();
    timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    if (maxWatcherTimeoutId != null) {
      window.clearTimeout(maxWatcherTimeoutId);
      maxWatcherTimeoutId = null;
    }
  };

  maxWatcherTimeoutId = window.setTimeout(
    stopWatcher,
    PICKER_Z_INDEX_WATCHER_MAX_MS
  );

  return stopWatcher;
};

const ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "video/mp4",
  "video/quicktime",
].join(",");

let scriptsLoaded = false;
let scriptsLoadingPromise = null;
let pickerReady = false;

const loadScript = (src) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });

export const loadGoogleScripts = async () => {
  if (scriptsLoaded && pickerReady) return;

  if (scriptsLoadingPromise) {
    await scriptsLoadingPromise;
    return;
  }

  scriptsLoadingPromise = (async () => {
    try {
      await Promise.all([
        loadScript(GOOGLE_API_SCRIPT),
        loadScript(GOOGLE_GSI_SCRIPT),
      ]);
      scriptsLoaded = true;

      await new Promise((resolve, reject) => {
        window.gapi.load("picker", {
          callback: () => {
            pickerReady = true;
            resolve();
          },
          onerror: () => {
            scriptsLoaded = false;
            pickerReady = false;
            reject(new Error("Failed to load Google Picker"));
          },
        });
      });
    } catch (error) {
      scriptsLoaded = false;
      pickerReady = false;
      throw error;
    }
  })();

  try {
    await scriptsLoadingPromise;
  } finally {
    scriptsLoadingPromise = null;
  }
};

export const openGoogleDrivePicker = ({
  accessToken,
  onPicked,
  onCancel = () => {},
  zIndex,
}) => {
  const google = window.google;

  const view = new google.picker.DocsView(google.picker.ViewId.DOCS)
    .setIncludeFolders(true)
    .setSelectFolderEnabled(false)
    .setMimeTypes(ALLOWED_MIME_TYPES);

  let stopPickerZIndexWatcher = () => {};

  const picker = new google.picker.PickerBuilder()
    .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
    .setAppId(import.meta.env.VITE_GOOGLE_DRIVE_APP_ID)
    .setDeveloperKey(import.meta.env.VITE_GOOGLE_DRIVE_API_KEY)
    .setOAuthToken(accessToken)
    .setLocale(getLanguageFormatted(i18next.language))
    .addView(view)
    .setCallback((data) => {
      if (data.action === google.picker.Action.PICKED) {
        stopPickerZIndexWatcher();
        const files = data.docs.map((doc) => ({
          id: doc.id,
          mimeType: doc.mimeType,
          name: doc.name,
        }));
        onPicked(files);
        return;
      }

      if (data.action === google.picker.Action.CANCEL) {
        stopPickerZIndexWatcher();
        onCancel();
      }
    })
    .build();

  picker.setVisible(true);
  stopPickerZIndexWatcher = startPickerZIndexWatcher({ zIndex });

  return picker;
};
